import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Info, LogOut, Settings, User } from 'lucide-react';
import { HeaderContext, LoginContext, LlmSetupBannerContext } from '../../utils/context';
import {
  NotificationBell,
  UserAvatar,
  LogoutIcon,
  ProfileIcon,
  SupportHeadset,
  // BlueLogo,
  settingsIcon,
  BellIconBlue,
  UserProfile2,
  MenuDownArrorw,
  OverviewIcon,
} from '../../assets/images';
import { DropdownListText } from '../constants';
import { headerProps } from './header.props';
import API from '../../services/api-middleware';
import { LoaderOverlay, MakeToast } from '../../components';
import {
  getUserConfigurations,
  applyUserConfigFromResponse,
} from '../../modules/end-user/user/services';
import { userConfigurationObject } from '../../modules/end-user/user/pages/users/user.props';
import Logout from '../../auth/protected/session-expire-modal';
import {
  getBasePermissionsFromStorage,
  LLM_PERMISSIONS_INVALIDATED_EVENT,
  redirectToFirstPermittedModule,
} from '../../services';
import { redirectToUnauthenticatedLanding } from '../../auth/unauthenticated-redirect';

const baseUrl = process.env.REACT_APP_USER_SERVER_URL;
const llmBaseUrl = process.env.REACT_APP_LLM_SERVER_URL;

function Header({ marginBottom, leftContent, rightContent }: headerProps) {
  const navigate = useNavigate();
  const { setIsLoggedIn } = useContext(LoginContext);
  const [budgetPermission, setBudgetPermission] = useState<boolean>(false);
  const [userProfile, setUserProfile] = useState({ name: '', role: '' });
  const [remainingBudget, setRemainingBudget] = useState<number>(0);
  const [, setTokensAlloted] = useState(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { userName, setUserName } = useContext(HeaderContext);
  const { showLlmSetupBanner, setShowLlmSetupBanner } = useContext(LlmSetupBannerContext);
  const [toggleClass, setToggleClass] = useState(false);
  const [chatHistoryOpen, setChatHistoryOpen] = useState(false);
  const [logOut, setLogout] = useState<boolean>(false);
  const [, setUserConfigurations] = useState<userConfigurationObject>({
    input_tokens_consumed: 0,
    output_tokens_consumed: 0,
    tokens_alloted: 0,
  });
  const dropdownRef = useRef<HTMLUListElement | null>(null);
  const chatHistoryButtonRef = useRef<HTMLButtonElement | null>(null);
  const location = useLocation();
  const isChecked = location.pathname.includes('system-admin');

  const updateUserDetails = () => {
    const userDetails = localStorage.getItem('user_details');
    if (userDetails) {
      const parsedUserDetails = JSON.parse(userDetails);
      setUserProfile({
        name: parsedUserDetails?.name || 'User',
        role: parsedUserDetails?.role || 'User',
      });
      setUserName(parsedUserDetails?.name || 'User');
    }

    setShowLlmSetupBanner(getBasePermissionsFromStorage().length === 0);

    const configurations = localStorage.getItem('configurations');
    if (configurations) {
      const parsedConfigurations = JSON.parse(configurations);
      const remaining =
        (parsedConfigurations?.tokens_alloted ?? 0) -
        ((parsedConfigurations?.input_tokens_consumed ?? 0) +
          (parsedConfigurations?.output_tokens_consumed ?? 0));
      setRemainingBudget(Number.isNaN(remaining) ? 0 : remaining);
      setTokensAlloted(parsedConfigurations?.tokens_alloted);
      setUserConfigurations({
        input_tokens_consumed: parsedConfigurations?.input_tokens_consumed ?? 0,
        output_tokens_consumed: parsedConfigurations?.output_tokens_consumed ?? 0,
        tokens_alloted: parsedConfigurations?.tokens_alloted ?? 0,
      });
    }
  };

  useEffect(() => {
    updateUserDetails();
    const handleStorageChange = () => {
      updateUserDetails();
    };
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const userDetails = localStorage.getItem('user_details');
  const isSuperAdminTrue = localStorage.getItem('is_super_admin');
  const parsedDetailsIsSuperAdminTrue = JSON.parse(isSuperAdminTrue!);
  let superAdmin: boolean;

  const logout = async () => {
    let token = localStorage.getItem('token');
    if (token !== null) {
      token = JSON.parse(token);
    }
    await API(baseUrl)
      .post(
        '/logout',
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        },
      )
      .then((res) => {
        if (res?.status === 200) {
          MakeToast('success', 'Logged out successfully');
        } else if (res?.status === 401) {
          redirectToUnauthenticatedLanding();
        }
      })
      .catch((err) => MakeToast('error', `Failed to logout: ${err.message}`));
  };

  const clearDB = async () => {
    let token = localStorage.getItem('token');
    if (token !== null) {
      token = JSON.parse(token);
    }
    let userId = localStorage.getItem('user_id');
    if (userId !== null) {
      userId = JSON.parse(userId);
    }
    await API(llmBaseUrl)
      .post(
        'clear_data',
        { userId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      .then(() => localStorage.clear())
      .catch((err) => MakeToast('error', `Failed to clear data: ${err.message}`));
  };

  const handleLogout = async () => {
    const session = localStorage.getItem('sessionExpired');
    setIsLoading(true);
    try {
      if (session) {
        await logout();
        await clearDB();
        setIsLoggedIn(false);

        redirectToUnauthenticatedLanding();
        localStorage.setItem('sessionExpired', 'false');
      } else {
        localStorage.setItem('sessionExpired', 'true');
      }
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 3000);
    }
  };

  useEffect(() => {
    if (userDetails) {
      const parsedDetails = JSON.parse(userDetails);
      setUserName(parsedDetails?.name);
      superAdmin = parsedDetails?.isSuperAdmin;
      let menuItems = [];
      if (superAdmin) {
        menuItems.push({
          name: superAdmin ? 'User' : 'Admin',
          // action: toggleView,
          icon: settingsIcon,
        });
        if (!superAdmin) {
          menuItems.push({
            name: 'Support',
            action: () => window.open('https://support.example.com', '_blank'),
            icon: SupportHeadset,
          });
        }
        menuItems.push({
          name: 'Notifications',
          action: () => {},
          icon: NotificationBell,
        });
        menuItems.push({
          name: 'Profile',
          action: () => navigate('/user-profile'),
          icon: ProfileIcon,
        });
        menuItems.push({
          name: 'Logout',
          action: handleLogout,
          icon: LogoutIcon,
        });
      } else {
        menuItems = [
          {
            name: 'Dashboard',
            action: () => navigate('/dashboard'),
            icon: UserAvatar,
          },
          {
            name: 'Profile',
            action: () => navigate('/user-profile'),
            icon: ProfileIcon,
          },
          { name: 'Logout', action: handleLogout, icon: LogoutIcon },
        ];
      }
    }
  }, [userDetails]);

  const handleBudget = () => {
    navigate('/budget-settings');
  };

  const handleProfile = () => {
    navigate('/user-profile');
  };

  const handleDropdown = () => {
    setToggleClass(!toggleClass);
  };

  useEffect(() => {
    const userPermissions = localStorage.getItem('permissions');
    const permissions = userPermissions && JSON.parse(userPermissions);
    if (permissions) {
      const modules = permissions.map((value: { module: string }) => value.module);
      if (modules.includes('Budget Settings')) {
        setBudgetPermission(true);
      }
    }
  }, []);

  const designArray = [
    {
      key: 'profile',
      name: DropdownListText[1],
      Img: User,
      onclick: handleProfile,
    },
    budgetPermission
      ? {
          key: 'settings',
          name: DropdownListText[2],
          Img: Settings,
          onclick: handleBudget,
        }
      : null,
    {
      key: 'logout',
      name: DropdownListText[3],
      Img: LogOut,
      onclick: handleLogout,
    },
  ]
    .filter((item) => item !== null)
    .filter((item) => {
      if (!isChecked) return true;
      return item.key !== 'profile' && item.key !== 'settings';
    });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event?.target as Node | null;
      if (dropdownRef.current && !dropdownRef.current?.contains(target)) {
        setToggleClass(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);

    // Listen for chat response completion to refresh budget from localStorage
    const handleChatComplete = () => {
      getUserConfigurations()
        .then((res) => {
          if (res?.status === 401) {
            setLogout(true);
            return;
          }
          applyUserConfigFromResponse(res);
          updateUserDetails();
        })
        .catch(() => {});
    };
    window.addEventListener('chatResponseComplete', handleChatComplete);

    const refreshHeaderFromStoredConfig = () => {
      updateUserDetails();
    };
    refreshHeaderFromStoredConfig();
    window.addEventListener(LLM_PERMISSIONS_INVALIDATED_EVENT, refreshHeaderFromStoredConfig);
    window.addEventListener('grigo-configuration-updated', refreshHeaderFromStoredConfig);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('chatResponseComplete', handleChatComplete);
      window.removeEventListener(LLM_PERMISSIONS_INVALIDATED_EVENT, refreshHeaderFromStoredConfig);
      window.removeEventListener('grigo-configuration-updated', refreshHeaderFromStoredConfig);
    };
  }, []);

  useEffect(() => {
    setShowLlmSetupBanner(getBasePermissionsFromStorage().length === 0);
    if (!location.pathname.includes('/chat')) {
      setChatHistoryOpen(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    const onChatHistoryOpenChanged = (event: Event) => {
      const customEvent = event as CustomEvent<boolean>;
      if (typeof customEvent.detail === 'boolean') {
        setChatHistoryOpen(customEvent.detail);
      }
    };
    window.addEventListener('chatHistoryOpenChanged', onChatHistoryOpenChanged);
    return () => {
      window.removeEventListener('chatHistoryOpenChanged', onChatHistoryOpenChanged);
    };
  }, []);

  const navigateToJira = () => {
    const jiraUrl = 'https://jira.grazitti.com/projects/GHD/summary';
    window.open(jiraUrl, '_blank');
  };

  const handleChatHistoryToggle = () => {
    if (chatHistoryButtonRef.current) {
      const rect = chatHistoryButtonRef.current.getBoundingClientRect();
      window.dispatchEvent(
        new CustomEvent('toggleChatHistory', {
          detail: { left: rect.left, top: rect.top + rect.height },
        }),
      );
    } else {
      window.dispatchEvent(new CustomEvent('toggleChatHistory'));
    }
  };

  const isChatPage = location.pathname.includes('/chat');
  const isLlmModule = location.pathname.includes('/llm');

  const handleToggleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;
    const existingDetails = JSON.parse(localStorage.getItem('user_details') || '{}');
    if (checked) {
      localStorage.setItem(
        'user_details',
        JSON.stringify({
          ...existingDetails,
          isSuperAdmin: true,
        }),
      );
      navigate('/system-admin');
    } else {
      const allPermissions = localStorage.getItem('permissions');
      const parsedPermissions: {
        module: string;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        llm: any; // or a more specific type if known
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        data: any;
      }[] = JSON.parse(allPermissions!);
      const userDetail = localStorage.getItem('user_details');
      const parsedUserDetails = userDetail ? JSON.parse(userDetail) : null;
      const isAdmin = parsedUserDetails?.isAdmin ?? false;
      redirectToFirstPermittedModule(isAdmin, parsedPermissions, navigate);
      localStorage.setItem(
        'user_details',
        JSON.stringify({
          ...existingDetails,
          isSuperAdmin: false,
        }),
      );
    }
  };

  return (
    <>
      {showLlmSetupBanner && !isChecked && !isLlmModule && (
        <div
          role="status"
          className="w-full shrink-0 border-b border-amber-300 bg-amber-100 px-6 py-2 text-sm text-amber-950 flex flex-wrap items-center justify-center gap-2 text-center"
        >
          <Info className="w-4 h-4" />
          <span>To get the most out of Grigo, add at least one LLM model for your account.</span>
          <button
            type="button"
            className="font-semibold text-[#F48B00] underline underline-offset-2 hover:text-[#d97a00]"
            onClick={() => navigate('/llm')}
          >
            Go to LLM
          </button>
        </div>
      )}
      <header
        className={`bg-[#F4F7FE] flex items-center justify-center flex-row w-full ${
          isChatPage ? 'h-14' : 'h-[70px]'
        } ${!isChecked && !isChatPage ? 'border-b border-[#E4E4E7]' : ''}`}
        style={{ marginBottom: marginBottom && marginBottom }}
      >
        <div
          className={`flex items-center justify-between bg-[#fff] h-full w-full px-6 gap-1 ${
            isChatPage ? 'border-b border-zinc-200' : 'shadow-md'
          }`}
        >
          {/* Left side - Dynamic content */}
          <div className="flex items-center gap-2.5 flex-1">{leftContent || null}</div>
          {/* Right side - Optional dynamic content (e.g. level switch, select user) */}
          {rightContent ? <div className="flex items-center gap-4 mr-4">{rightContent}</div> : null}
          {/* Right side - Static content (Chat History, Support, Notifications, User profile) */}
          <div
            className={`flex items-center cursor-pointer relative justify-end ${
              isChatPage ? `gap-1 ${toggleClass ? 'z-50' : 'z-30'}` : 'z-[999] gap-[5px]'
            }`}
          >
            {isChatPage && (
              <button
                ref={chatHistoryButtonRef}
                type="button"
                data-tour="chat-tour-history"
                className={`flex items-center gap-2 h-9 px-3.5 rounded-full text-[13px] font-medium transition-colors ${
                  chatHistoryOpen
                    ? 'bg-[#FFF4E5] text-[#F48B00] hover:bg-[#FFE8CC]'
                    : 'text-zinc-700 hover:bg-zinc-100'
                }`}
                onClick={handleChatHistoryToggle}
                aria-label="Chat History"
                aria-pressed={chatHistoryOpen}
              >
                <img
                  src={OverviewIcon}
                  alt=""
                  className="w-4 h-4"
                  style={{
                    filter: chatHistoryOpen
                      ? 'brightness(0) saturate(100%) invert(56%) sepia(98%) saturate(1000%) hue-rotate(360deg) brightness(103%) contrast(101%)'
                      : 'brightness(0) saturate(100%) invert(38%)',
                  }}
                />
                <span>Chat History</span>
              </button>
            )}
            {!isChecked && (
              <button
                type="button"
                className={
                  isChatPage
                    ? 'flex items-center justify-center w-9 h-9 rounded-full hover:bg-zinc-100 transition-colors group'
                    : 'flex items-center border-r-2 px-2.5 h-[30px] hover:bg-[#f48b00] hover:rounded-md hover:h-[40px] opacity-70 hover:opacity-100 transition-all duration-300 group'
                }
                onClick={navigateToJira}
                aria-label="Support"
              >
                <img
                  className={
                    isChatPage
                      ? 'w-5 h-5 object-contain opacity-70 group-hover:opacity-100'
                      : 'h-6 w-6 font-bold object-contain group-hover:brightness-0 group-hover:saturate-100 group-hover:invert transition-all duration-300'
                  }
                  src={SupportHeadset}
                  alt="Support headset"
                />
              </button>
            )}
            <div
              className={
                isChatPage
                  ? 'flex items-center justify-center w-9 h-9 rounded-full hover:bg-zinc-100 transition-colors group'
                  : 'flex items-center px-2.5 border-r-2 h-[30px] hover:bg-[#f48b00] hover:rounded-md hover:h-[40px] opacity-70 hover:opacity-100 transition-all duration-300 group'
              }
            >
              <img
                src={BellIconBlue}
                alt="Notifications"
                className={
                  isChatPage
                    ? 'w-5 h-5 object-contain opacity-70 group-hover:opacity-100'
                    : 'h-6 w-6 font-bold object-contain group-hover:brightness-0 group-hover:saturate-100 group-hover:invert transition-all duration-300'
                }
              />
            </div>
            <div className="relative">
              <button
                type="button"
                className={
                  isChatPage
                    ? 'flex flex-row items-center h-9 pl-1 pr-2.5 rounded-full hover:bg-zinc-100 transition-colors'
                    : 'flex flex-row items-center p-2.5 h-[40px] hover:text-white hover:bg-[#f48b00] hover:rounded-md opacity-70 hover:opacity-100 transition-all duration-300 group'
                }
                onClick={handleDropdown}
                tabIndex={0}
                aria-label="dropdown"
              >
                <span
                  className={
                    isChatPage
                      ? 'flex items-center justify-center w-7 h-7 mr-1.5 shrink-0'
                      : 'text-[#000] flex items-center justify-center h-[24px] w-[24px] mr-2.5 rounded-[8px] border-[#0A1116] ml-2 flex-shrink-0'
                  }
                >
                  <img
                    src={UserProfile2}
                    alt="User profile"
                    className={
                      isChatPage
                        ? 'w-7 h-7'
                        : 'w-8 h-8 group-hover:brightness-0 group-hover:saturate-100 group-hover:invert transition-all duration-300'
                    }
                  />
                </span>
                <div className="flex items-center">
                  <span
                    className={isChatPage ? 'text-sm font-medium text-zinc-800' : 'font-medium'}
                  >
                    {userName}
                  </span>
                  <span className="ml-2 flex items-center">
                    <img
                      src={MenuDownArrorw}
                      alt="Dropdown arrow"
                      className={
                        isChatPage
                          ? 'opacity-70'
                          : 'group-hover:brightness-0 group-hover:saturate-100 group-hover:invert transition-all duration-300'
                      }
                    />
                  </span>
                </div>
              </button>
            </div>
            <ul
              ref={dropdownRef}
              className={`w-64 p-0 bg-white border border-[#E4E4E7] shadow-xl rounded-xl overflow-hidden absolute transition-all duration-300 ease-in-out z-[998] top-[48px] right-[-18px] flex flex-col ${
                toggleClass
                  ? 'translate-y-0 opacity-100'
                  : 'translate-y-[-8px] opacity-0 pointer-events-none'
              }`}
            >
              {/* User info header */}
              <li className={`p-3 ${!isChecked ? 'border-b border-[#E4E4E7]' : ''}`}>
                <p className="text-sm font-bold text-[#101828]">{userProfile?.name || userName}</p>
                <div className="flex items-center gap-2 mt-1.5">
                  <span className="text-[10px] font-bold uppercase tracking-wide bg-[#F48B00] text-white px-2 py-0.5 rounded">
                    {userProfile?.role || 'user'}
                  </span>
                </div>
                {parsedDetailsIsSuperAdminTrue && (
                  <div className="flex items-center gap-2 mt-1.5">
                    <span
                      className={`text-xs font-medium ${!isChecked ? 'text-[#0A8BC7]' : 'text-[#6A6A6A]'}`}
                    >
                      User
                    </span>
                    <span className="switchForApp">
                      <input
                        type="checkbox"
                        id="super-admin-toggle"
                        name="superAdminToggle"
                        onChange={(e) => handleToggleChange(e)}
                        checked={isChecked}
                      />
                      <span className="sliderForApp round" />
                    </span>
                    <span
                      className={`text-xs font-medium ${isChecked ? 'text-[#0A8BC7]' : 'text-[#6A6A6A]'}`}
                    >
                      Admin
                    </span>
                  </div>
                )}
                <p className="text-xs text-[#6A6A6A] mt-1.5">
                  {`$${Math.max(0, remainingBudget).toFixed(2)} left`}
                </p>
              </li>

              {/* Menu items (non-Logout) */}
              {!isChecked && (
                <li className="py-1.5">
                  {designArray
                    ?.filter((data) => data?.key !== 'logout')
                    ?.map((data) => {
                      const Icon = data?.Img;
                      return (
                        <button
                          type="button"
                          key={data?.key}
                          className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-[#101828] hover:bg-[#F4F7FE] transition-colors"
                          onClick={data?.onclick}
                        >
                          {Icon && <Icon className="w-4 h-4 opacity-70" aria-hidden />}
                          <span>{data?.name}</span>
                        </button>
                      );
                    })}
                </li>
              )}

              {/* Logout - always visible so user can log out from system-admin too */}
              {designArray?.some((data) => data?.key === 'logout') && (
                <>
                  <div className="border-t border-[#E4E4E7]" />
                  <li className="py-1.5">
                    {designArray
                      ?.filter((data) => data?.key === 'logout')
                      ?.map((data) => {
                        const Icon = data?.Img;
                        return (
                          <button
                            type="button"
                            key={data?.key}
                            className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-[#DC2626] hover:bg-[#FEE2E2] transition-colors"
                            onClick={data?.onclick}
                          >
                            {Icon && <Icon className="w-4 h-4" aria-hidden />}
                            <span>{data?.name}</span>
                          </button>
                        );
                      })}
                  </li>
                </>
              )}
            </ul>
          </div>
        </div>
      </header>
      {isLoading && createPortal(<LoaderOverlay />, document.body)}
      {logOut && createPortal(<Logout />, document.body)}
    </>
  );
}

export default Header;
